import { Link } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative"
      style={{ background: 'var(--bg-surface)', borderTop: '1px solid rgba(0,240,255,0.08)' }}
    >
      {/* Gradient line */}
      <div className="gradient-line" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <FlaskConical className="w-6 h-6" style={{ color: 'var(--neon-cyan)' }} />
              <div className="flex flex-col leading-none">
                <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  APOLLO
                </span>
                <span className="text-[9px] tracking-[0.25em] uppercase" style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace" }}>
                  COMPOUNDS
                </span>
              </div>
            </Link>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              US-based supplier of premium research-grade peptides and compounds. Committed to advancing scientific discovery through quality and precision.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold tracking-[0.15em] uppercase mb-4" style={{ fontFamily: "'Rajdhani', sans-serif", color: 'var(--neon-cyan)' }}>
              Navigation
            </h4>
            <div className="flex flex-col gap-2.5">
              {[
                { to: "/", label: "Home" },
                { to: "/products", label: "Products" },
                { to: "/about", label: "About Us" },
                { to: "/faq", label: "FAQ" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="text-xs transition-colors duration-300 hover:text-white"
                  style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-sm font-semibold tracking-[0.15em] uppercase mb-4" style={{ fontFamily: "'Rajdhani', sans-serif", color: 'var(--neon-cyan)' }}>
              Research Compounds
            </h4>
            <div className="flex flex-col gap-2.5">
              {["GLP-3R", "GLP-2T", "GHK-Cu", "Glow", "MOTS-c", "NAD+", "Semax"].map((name) => (
                <Link
                  key={name}
                  to={`/products/${name === "GLP-3R" ? "retatrutide" : name === "GLP-2T" ? "tirzepatide" : name.toLowerCase().replace(/\+/g, '-plus').replace(/\s/g, '-')}`}
                  className="text-xs transition-colors duration-300 hover:text-white"
                  style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}
                >
                  {name}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal / Contact */}
          <div>
            <h4 className="text-sm font-semibold tracking-[0.15em] uppercase mb-4" style={{ fontFamily: "'Rajdhani', sans-serif", color: 'var(--neon-cyan)' }}>
              Legal
            </h4>
            <div className="flex flex-col gap-3">
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                All products are intended for laboratory and research use only. Not for human consumption, diagnostic, or therapeutic use.
              </p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                apollocompounds.com
              </p>
            </div>
          </div>
        </div>

        <Separator className="my-8" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[11px] tracking-wide" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>
            &copy; {new Date().getFullYear()} Apollo Compounds. All rights reserved.
          </p>
          <div
            data-testid="footer-research-disclaimer"
            className="text-[11px] tracking-[0.15em] uppercase px-4 py-1.5 rounded"
            style={{
              color: 'var(--neon-magenta)',
              border: '1px solid rgba(229,0,255,0.2)',
              background: 'rgba(229,0,255,0.05)',
              fontFamily: "'Roboto Mono', monospace",
            }}
          >
            For Research Purposes Only
          </div>
        </div>
      </div>
    </footer>
  );
}
